/**
 * VoiceControls: 底部居中的麦克风按钮，控制自己是否开麦（默认关麦）。
 * 静音态图标带斜杠；开麦态沿用 HUD 玉青高亮。仅联机模式且成功加入语音
 * 频道后显示；编辑器打开时与其它 HUD 一起隐藏。
 */

import type { VoiceController } from '../voice/LazyVoice';

class VoiceControls {
    private _el: HTMLElement;
    private _btn: HTMLButtonElement;
    private _slash: SVGLineElement;
    private _joined = false;
    private _editorHidden = false;

    constructor(private _voice: VoiceController) {
        this._el = document.createElement('div');
        this._el.id = 'voice-controls';
        this._el.className = 'hidden';

        this._btn = document.createElement('button');
        this._btn.id = 'voice-mic';
        this._btn.className = 'hud-btn';
        this._btn.type = 'button';
        this._btn.title = '开麦（其他人可听到你）';
        this._btn.setAttribute('aria-pressed', 'false');
        this._btn.setAttribute('aria-label', '麦克风开关');

        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('width', '26');
        svg.setAttribute('height', '26');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
        svg.setAttribute('stroke-width', '1.8');
        svg.setAttribute('stroke-linecap', 'round');
        const capsule = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        capsule.setAttribute('x', '9'); capsule.setAttribute('y', '3');
        capsule.setAttribute('width', '6'); capsule.setAttribute('height', '11');
        capsule.setAttribute('rx', '3');
        const arc = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        arc.setAttribute('d', 'M5 11a7 7 0 0 0 14 0');
        const stem = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        stem.setAttribute('d', 'M12 18v3');
        this._slash = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        this._slash.classList.add('voice-mic-slash');
        this._slash.setAttribute('x1', '4'); this._slash.setAttribute('y1', '4');
        this._slash.setAttribute('x2', '20'); this._slash.setAttribute('y2', '20');
        svg.append(capsule, arc, stem, this._slash);

        this._btn.appendChild(svg);
        this._btn.addEventListener('click', () => { void this._voice.toggleMic(); });
        this._el.appendChild(this._btn);
        (document.getElementById('ui-layer') ?? document.body).appendChild(this._el);

        this._voice.onMicState = () => this._sync();
    }

    /** 语音频道加入/离开后调用。 */
    setJoined(joined: boolean): void {
        this._joined = joined;
        this._sync();
    }

    /** 编辑器打开时与其它 HUD 控件一起隐藏。 */
    setVisible(visible: boolean): void {
        this._editorHidden = !visible;
        this._sync();
    }

    private _sync(): void {
        const on = this._voice.micEnabled;
        this._btn.classList.toggle('active', on);
        this._btn.setAttribute('aria-pressed', String(on));
        this._slash.style.display = on ? 'none' : '';
        this._btn.title = on ? '闭麦（其他人将听不到你）' : '开麦（其他人可听到你）';
        this._el.classList.toggle('hidden', !this._joined || this._editorHidden);
    }

    destroy(): void {
        this._el.remove();
    }
}

export { VoiceControls };
